import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { useToast } from '../../../hooks/useToast';
import {
  ArrowLeftIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import RichTextEditor from '../../../components/RichTextEditor';
import { cloudinaryConfig } from '../../../config/cloudinary';

interface HowItWorksSectionData {
  title: string;
  subtitle: string;
  videoUrl: string;
  videoCaption: string;
  enabled: boolean;
}

interface Step {
  id: string;
  number: number;
  title: string;
  description: string;
  order: number;
  enabled: boolean;
}

const HowItWorksEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  const [sectionData, setSectionData] = useState<HowItWorksSectionData>({
    title: '',
    subtitle: '',
    videoUrl: '',
    videoCaption: '',
    enabled: true,
  });

  const [steps, setSteps] = useState<Step[]>([]);
  const [editingStep, setEditingStep] = useState<Step | null>(null);
  const [showStepModal, setShowStepModal] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load section data
      const sectionRef = doc(db, 'landingPageContent', 'howItWorksSection');
      const sectionSnap = await getDoc(sectionRef);
      if (sectionSnap.exists()) {
        setSectionData(sectionSnap.data() as HowItWorksSectionData);
      }

      // Load steps
      const stepsRef = collection(
        db,
        'landingPageContent',
        'howItWorksSection',
        'steps'
      );
      const stepsSnap = await getDocs(stepsRef);

      const stepsData: Step[] = [];
      stepsSnap.forEach((d) => {
        stepsData.push({ id: d.id, ...(d.data() as Omit<Step, 'id'>) } as Step);
      });

      setSteps(stepsData.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    } catch (error) {
      console.error('Error loading how it works data:', error);
      showToast({ message: 'Failed to load data', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // ---------- Cloudinary Upload ----------
  const uploadToCloudinary = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', cloudinaryConfig.uploadPreset);
    formData.append('folder', 'landing-page/videos');

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${cloudinaryConfig.cloudName}/video/upload`,
      {
        method: 'POST',
        body: formData,
      }
    );

    if (!response.ok) throw new Error('Upload failed');

    const data = await response.json();
    return data.secure_url;
  };

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 100MB limit
    if (file.size > 100 * 1024 * 1024) {
      showToast({
        message: 'Video size should be less than 100MB',
        type: 'error',
      });
      return;
    }

    setUploadingVideo(true);
    try {
      const url = await uploadToCloudinary(file);
      setSectionData((prev) => ({ ...prev, videoUrl: url }));
      showToast({ message: 'Video uploaded successfully', type: 'success' });
    } catch (error) {
      console.error('Error uploading video:', error);
      showToast({ message: 'Failed to upload video', type: 'error' });
    } finally {
      setUploadingVideo(false);
    }
  };

  // ---------- Save Section ----------
  const handleSectionSave = async () => {
    setSaving(true);
    try {
      const sectionRef = doc(db, 'landingPageContent', 'howItWorksSection');
      await setDoc(sectionRef, {
        ...sectionData,
        lastUpdated: serverTimestamp(),
      });

      showToast({ message: 'Section updated successfully', type: 'success' });
    } catch (error) {
      console.error('Error saving section data:', error);
      showToast({ message: 'Failed to save section', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ---------- Steps CRUD ----------
  const openAddStepModal = () => {
    const nextNumber =
      steps.length > 0 ? Math.max(...steps.map((s) => s.number ?? 0)) + 1 : 1;
    const nextOrder = steps.length + 1;

    setEditingStep({
      id: '',
      number: nextNumber,
      title: '',
      description: '',
      order: nextOrder,
      enabled: true,
    });
    setShowStepModal(true);
  };

  const openEditStepModal = (step: Step) => {
    setEditingStep(step);
    setShowStepModal(true);
  };

  const handleStepSave = async (step: Step) => {
    try {
      const stepId = step.id || `step-${Date.now()}`;
      const stepRef = doc(
        db,
        'landingPageContent',
        'howItWorksSection',
        'steps',
        stepId
      );

      await setDoc(stepRef, {
        number: step.number,
        title: step.title,
        description: step.description,
        order: step.order,
        enabled: step.enabled,
        lastUpdated: serverTimestamp(),
      });

      showToast({ message: 'Step saved successfully', type: 'success' });

      setShowStepModal(false);
      setEditingStep(null);
      loadData();
    } catch (error) {
      console.error('Error saving step:', error);
      showToast({ message: 'Failed to save step', type: 'error' });
    }
  };

  const handleStepDelete = async (stepId: string) => {
    if (!confirm('Are you sure you want to delete this step?')) return;

    try {
      const stepRef = doc(
        db,
        'landingPageContent',
        'howItWorksSection',
        'steps',
        stepId
      );
      await deleteDoc(stepRef);

      showToast({ message: 'Step deleted successfully', type: 'success' });
      loadData();
    } catch (error) {
      console.error('Error deleting step:', error);
      showToast({ message: 'Failed to delete step', type: 'error' });
    }
  };

  // ---------- UI ----------
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/cms')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            type="button"
          >
            <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
          </button>

          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Edit How It Works Section
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Steps and demo video section
            </p>
          </div>
        </div>
      </div>

      {/* Section Header Card */}
      <div className="bg-white shadow rounded-lg p-6 space-y-6">
        <h2 className="text-lg font-semibold text-gray-900">Section Header</h2>

        <RichTextEditor
          value={sectionData.title}
          onChange={(value) => setSectionData({ ...sectionData, title: value })}
          placeholder="e.g., How it works"
          label="Section Title"
          required
        />

        <RichTextEditor
          value={sectionData.subtitle}
          onChange={(value) =>
            setSectionData({ ...sectionData, subtitle: value })
          }
          placeholder="e.g., Three simple steps to stay protected..."
          label="Subtitle"
          required
        />

        {/* Video Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Demo Video
          </label>

          <div className="space-y-4">
            {sectionData.videoUrl && (
              <div className="relative w-full rounded-lg overflow-hidden bg-gray-900">
                <video
                  src={sectionData.videoUrl}
                  controls
                  className="w-full"
                  style={{ maxHeight: '400px' }}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            )}

            <div>
              <input
                type="file"
                accept="video/*"
                onChange={handleVideoUpload}
                disabled={uploadingVideo}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0
                  file:text-sm file:font-semibold file:bg-primary file:text-white
                  hover:file:bg-primary-dark file:cursor-pointer disabled:opacity-50"
              />
              <p className="mt-1 text-sm text-gray-500">
                {uploadingVideo
                  ? 'Uploading video to Cloudinary...'
                  : 'MP4, WebM, or MOV - Max 100MB'}
              </p>
            </div>
          </div>
        </div>

        {/* Video Caption */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Video Caption
          </label>
          <input
            type="text"
            value={sectionData.videoCaption}
            onChange={(e) =>
              setSectionData({ ...sectionData, videoCaption: e.target.value })
            }
            placeholder="e.g., Video demo and marketing video for our application"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="section-enabled"
            checked={sectionData.enabled}
            onChange={(e) =>
              setSectionData({ ...sectionData, enabled: e.target.checked })
            }
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label
            htmlFor="section-enabled"
            className="ml-2 block text-sm text-gray-700"
          >
            Enable this section
          </label>
        </div>

        <button
          onClick={handleSectionSave}
          disabled={saving}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
          type="button"
        >
          {saving ? 'Saving...' : 'Save Section'}
        </button>
      </div>

      {/* Steps */}
      <div className="bg-white shadow rounded-lg p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Steps</h2>
          <button
            type="button"
            onClick={openAddStepModal}
            className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Add Step</span>
          </button>
        </div>

        {steps.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            No steps configured yet. Click "Add Step" to create one.
          </p>
        ) : (
          <div className="space-y-4">
            {steps.map((step) => (
              <div
                key={step.id}
                className="border border-gray-200 rounded-lg p-4 flex items-start justify-between"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xl font-bold">
                      {step.number}
                    </span>
                  </div>

                  <div className="flex-1">
                    <div
                      className="font-semibold text-gray-900"
                      dangerouslySetInnerHTML={{ __html: step.title }}
                    />
                    <div
                      className="text-sm text-gray-600 mt-1"
                      dangerouslySetInnerHTML={{ __html: step.description }}
                    />
                    <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                      <span>Order: {step.order}</span>
                      <span
                        className={
                          step.enabled ? 'text-green-600' : 'text-red-600'
                        }
                      >
                        {step.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  <button
                    type="button"
                    onClick={() => openEditStepModal(step)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleStepDelete(step.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Step Modal */}
      {showStepModal && editingStep && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {editingStep.id
                ? `Edit Step ${editingStep.number}`
                : 'Add New Step'}
            </h2>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Step Number
                </label>
                <input
                  type="number"
                  value={editingStep.number}
                  onChange={(e) =>
                    setEditingStep({
                      ...editingStep,
                      number: parseInt(e.target.value || '1', 10),
                    })
                  }
                  min={1}
                  max={20}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <RichTextEditor
                value={editingStep.title}
                onChange={(value) =>
                  setEditingStep({ ...editingStep, title: value })
                }
                placeholder="e.g., Download & Install"
                label="Title"
                required
              />

              <RichTextEditor
                value={editingStep.description}
                onChange={(value) =>
                  setEditingStep({ ...editingStep, description: value })
                }
                placeholder="Describe this step..."
                label="Description"
                required
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Order
                </label>
                <input
                  type="number"
                  value={editingStep.order}
                  onChange={(e) =>
                    setEditingStep({
                      ...editingStep,
                      order: parseInt(e.target.value || '1', 10),
                    })
                  }
                  min={1}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="step-enabled"
                  checked={editingStep.enabled}
                  onChange={(e) =>
                    setEditingStep({
                      ...editingStep,
                      enabled: e.target.checked,
                    })
                  }
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
                <label
                  htmlFor="step-enabled"
                  className="ml-2 block text-sm text-gray-700"
                >
                  Enable this step
                </label>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowStepModal(false);
                  setEditingStep(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleStepSave(editingStep)}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
              >
                Save Step
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HowItWorksEditorPage;
